import React from "react";
import { isValid } from "date-fns";
import { FormField, useFieldValue } from "../../../molecules/FormField";
import { DataTableColumn, ColumnType } from "../types";
import { useTranslate } from "../../../util/localization";

export interface FilterInputProps {
  column: DataTableColumn;
  value: any;
  onChange: (value: any) => void;
  operator?: string;
  onSubmit?: () => void;
}

const NO_CONSTRAINTS = {};

function FilterInputDateTime({
  id,
  value,
  onChange,
  format,
  dateOnly,
}: {
  id: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  format: string;
  dateOnly?: boolean;
}) {
  const field = useFieldValue<Date | null, { input?: string }>(value);
  return (
    <FormField.DateTime
      id={id}
      value={field.value}
      data={field.data}
      onChangeField={(fid, date, opts) => {
        field.onChangeField(fid, date, opts);
        onChange(date);
      }}
      constraints={NO_CONSTRAINTS}
      format={format}
      dateOnly={dateOnly}
      noLabel
      dense
    />
  );
}

export function FilterInput({
  column,
  value,
  onChange,
  onSubmit,
}: FilterInputProps) {
  const id = `filter-${column.id}`;
  const [t] = useTranslate();

  switch (column.type) {
    case ColumnType.BOOL:
      return (
        <FormField.Switch
          id={id}
          value={value === "true"}
          onChangeField={(_, checked) => onChange(checked ? "true" : "false")}
          constraints={NO_CONSTRAINTS}
          noLabel
          dense
        />
      );

    case ColumnType.INTEGER:
      return (
        <FormField.Integer
          id={id}
          value={value != null ? String(value) : null}
          onChangeField={(_, v) => onChange(v)}
          onQuickSubmit={onSubmit}
          constraints={NO_CONSTRAINTS}
          noLabel
          dense
        />
      );

    case ColumnType.FLOAT:
      return (
        <FormField.Float
          id={id}
          value={value != null ? String(value) : null}
          onChangeField={(_, v) => onChange(v)}
          onQuickSubmit={onSubmit}
          constraints={NO_CONSTRAINTS}
          noLabel
          dense
        />
      );

    case ColumnType.ENUM: {
      const enumOptions = column.options ?? [];
      const selectedOption =
        enumOptions.find((o) => String(o.value) === value) ?? null;

      return (
        <FormField.Select
          id={id}
          value={selectedOption}
          onChangeField={(_, opt) => {
            if (Array.isArray(opt)) return;
            onChange(opt ? String(opt.value) : "");
          }}
          options={enumOptions}
          constraints={NO_CONSTRAINTS}
          clearable
          noLabel
          dense
        />
      );
    }

    case ColumnType.DATE:
    case ColumnType.LOCALDATE: {
      const dateValue = value instanceof Date && isValid(value) ? value : null;
      return (
        <FilterInputDateTime
          key={dateValue?.getTime() ?? "null"}
          id={id}
          value={dateValue}
          onChange={onChange}
          format="dd.MM.yyyy"
          dateOnly
        />
      );
    }

    case ColumnType.DATETIME: {
      const dateValue = value instanceof Date && isValid(value) ? value : null;
      return (
        <FilterInputDateTime
          key={dateValue?.getTime() ?? "null"}
          id={id}
          value={dateValue}
          onChange={onChange}
          format="dd.MM.yyyy HH:mm"
        />
      );
    }

    case ColumnType.TEXT:
    case ColumnType.LOCALIZED:
    case ColumnType.TIME:
    default:
      return (
        <FormField.Text
          id={id}
          value={value || null}
          onChangeField={(_, v) => onChange(v ?? "")}
          onQuickSubmit={onSubmit}
          quickSubmitKey="enter"
          constraints={NO_CONSTRAINTS}
          clearable={false}
          noLabel
          dense
        />
      );
  }
}
